import { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { routes } from '@/navigation/routes.tsx';
import LogoPrimaryShape from '@/components/common/svg/shapes/LogoPrimaryShape.tsx';
import { TelegramCustomStar } from '@/components/common/svg/telegram/TelegramCustomStar.tsx';
import { TelegramLogo } from '@/components/common/svg/telegram/TelegramLogo.tsx';
import HeartSimpleShape from '@/components/common/svg/shapes/HeartSimpleShape.tsx';
import UIConfig from '@/UIConfig.ts';
import { useHeaderStore } from '@/store/useHeadersStylesStore.ts';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';

interface Shape {
  id: number;
  x: number; // Percentage for left position
  y: number; // Percentage for top position
  opacity: number;
  size: number;
  type: 'star' | 'heart' | 'telegram';
  color: string;
  rotation: number; // Rotation in degrees
}

const RegistrationWelcomePage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setLogoVisible } = useHeaderStore();

  // Generate shapes with edge-focused positioning, no overlap, and no identical types nearby
  const [shapes] = useState<Shape[]>(() => {
    const shapeCount = 15; // 15 shapes
    const shapesArray: Shape[] = [];
    const minDistance = 15; // Minimum distance between any shapes (in %)
    const sameTypeMinDistance = 15; // Minimum distance between same-type shapes (in %)
    const edgeMargin = 10; // Percentage margin for edge placement
    const maxY = 70; // Limit to upper 70% to avoid white bottom area
    const viewportWidth = 360; // Approximate viewport width in pixels
    const viewportHeight = 640; // Approximate viewport height in pixels

    const generatePosition = (size: number) => {
      const edge = Math.floor(Math.random() * 3); // 0: left, 1: right, 2: top
      let x: number, y: number;

      // Convert size to percentage to ensure shapes stay within bounds
      const sizeInPercentX = (size / viewportWidth) * 100;
      const sizeInPercentY = (size / viewportHeight) * 100;

      if (edge === 0) {
        // Left edge
        x = Math.random() * edgeMargin; // 0% to 10%
        y = Math.random() * (maxY - sizeInPercentY); // Adjust to keep shape in bounds
      } else if (edge === 1) {
        // Right edge
        x = 100 - edgeMargin - sizeInPercentX + Math.random() * edgeMargin; // 90% to 100% - size
        y = Math.random() * (maxY - sizeInPercentY);
      } else {
        // Top edge
        x = Math.random() * (100 - sizeInPercentX); // Adjust to keep shape in bounds
        y = Math.random() * edgeMargin; // 0% to 10%
      }

      // Ensure x and y are within 0% to 100% and 0% to maxY
      x = Math.max(0, Math.min(x, 100 - sizeInPercentX));
      y = Math.max(0, Math.min(y, maxY - sizeInPercentY));

      return { x, y };
    };

    for (let i = 0; i < shapeCount; i++) {
      const size = 10 + Math.random() * 15; // Size between 10 and 25 pixels
      let { x, y } = generatePosition(size);
      let attempts = 0;
      const maxAttempts = 50; // Increased to account for stricter constraints
      let isValidPosition = false;

      // Randomly select type: star, heart, or telegram
      const random = Math.random();
      const type =
        random < 0.33 ? 'star' : random < 0.66 ? 'heart' : 'telegram';

      while (!isValidPosition && attempts < maxAttempts) {
        isValidPosition = true;

        // Check for overlap and same-type proximity with existing shapes
        for (const existingShape of shapesArray) {
          const distance = Math.sqrt(
            Math.pow(x - existingShape.x, 2) + Math.pow(y - existingShape.y, 2)
          );
          if (
            distance < minDistance ||
            (type === existingShape.type && distance < sameTypeMinDistance)
          ) {
            // Reject if too close to any shape or too close to same-type shape
            isValidPosition = false;
            ({ x, y } = generatePosition(size));
            break;
          }
        }
        attempts++;
      }

      if (isValidPosition) {
        shapesArray.push({
          id: i,
          x,
          y,
          opacity: 0.3 + Math.random() * 0.3, // Opacity between 0.3 and 0.6
          size,
          type,
          color: Math.random() > 0.5 ? '#4A4A4A' : '#6B7280', // Muted gray tones
          rotation: Math.random() * 30 - 15 // Random rotation between -15 and 15 degrees
        });
      }
    }
    return shapesArray;
  });

  const handleClick = () => {
    navigate(routes.registrationUserData);
  };

  useEffect(() => {
    return () => {
      setLogoVisible(true);
    };
  }, []);

  return (
    <div style={pageStyles}>
      <div style={backgroundStyles}>
        {/* Render scattered shapes */}
        {shapes.map((shape) => (
          <motion.div
            key={shape.id}
            style={{
              position: 'absolute',
              left: `${shape.x}%`,
              top: `${shape.y}%`,
              opacity: shape.opacity,
              zIndex: 1 // Behind the logo
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: shape.opacity,
              scale: 1,
              rotate: shape.rotation
            }}
            transition={{ duration: 0.8, delay: shape.id * 0.05 }}
          >
            {shape.type === 'star' ? (
              <TelegramCustomStar
                fillColor={shape.color}
                height={shape.size}
                width={shape.size}
              />
            ) : shape.type === 'heart' ? (
              <HeartSimpleShape
                fillColor={shape.color}
                height={shape.size}
                width={shape.size}
              />
            ) : (
              <TelegramLogo
                fillColor={shape.color}
                height={shape.size}
                width={shape.size}
              />
            )}
          </motion.div>
        ))}
      </div>

      <div style={logoContainerStyles}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <LogoPrimaryShape width={350} height={350} animated={false} />
        </motion.div>
      </div>

      <div style={bottomBlockStyles}>
        <div style={contentStyles}>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            style={subtitleStyles}
          >
            {t('registration.welcome.subtitle')}
          </motion.p>
        </div>
      </div>
      <MainButton
        onClick={handleClick}
        text={t('button.register')}
        fixed
        containerStyle={buttonContainerStyle}
      />
    </div>
  );
};

const pageStyles: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  overflow: 'hidden'
};

const backgroundStyles: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: UIConfig.Colors.BackgroundDark,
  backgroundSize: 'cover',
  backgroundPosition: 'top center',
  backgroundRepeat: 'no-repeat',
  zIndex: 1
};

const logoContainerStyles: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2
};

const bottomBlockStyles: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  minHeight: '30%',
  backgroundColor: '#FFFFFF',
  zIndex: 3,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingBottom: '88px'
};

const contentStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: '400px',
  width: '100%',
  padding: '20px'
};

const buttonContainerStyle: CSSProperties = {
  backgroundColor: '#FFFFFF',
  zIndex: 4
};

const subtitleStyles: CSSProperties = {
  fontSize: '17px',
  fontWeight: '400',
  color: '#6B6B6E',
  maxWidth: '90%',
  margin: '0',
  textAlign: 'center',
  lineHeight: '1.6',
  letterSpacing: '-0.2px'
};

export default RegistrationWelcomePage;
